import { useState, useMemo } from 'react';
import { endOfMonth, isWithinInterval, startOfMonth } from 'date-fns';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  IconButton,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChatHistoryDialog from './ChatHistoryDialog';
import NaoAtendidoDiagnosticoDialog from './NaoAtendidoDiagnosticoDialog';
import { useWSContext } from '../context/WebSocketContext';

const dotSx = {
  width: 4,
  height: 4,
  bgcolor: 'currentColor',
  borderRadius: '50%',
  mr: 1,
  display: 'inline-block',
};

const formatarDuracao = (segundos) => {
  if (segundos === null || segundos === undefined) return '';
  const mins = Math.floor(segundos / 60);
  const secs = segundos % 60;
  return `${mins}m ${secs}s`;
};

const formatarTempoFila = (item) => {
  const tempoFilaMin = Number(item?.tempoFilaMin);
  if (Number.isFinite(tempoFilaMin) && tempoFilaMin >= 0) {
    return `${tempoFilaMin} min`;
  }

  const tempoMs = Number(item?.tempoNaFilaMs ?? item?.tempoEsperaMs);
  if (Number.isFinite(tempoMs) && tempoMs >= 0) {
    const totalSegundos = Math.round(tempoMs / 1000);
    const mins = Math.floor(totalSegundos / 60);
    const secs = totalSegundos % 60;
    return `${mins}m ${secs}s`;
  }

  return 'N/A';
};

const formatarMesAno = (data) => {
  const mes = data
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '');
  const mesCapitalizado = mes.charAt(0).toUpperCase() + mes.slice(1).toLowerCase();
  return `${mesCapitalizado}/${data.getFullYear()}`;
};

const formatarHora = (valor) => {
  if (!valor) return 'N/A';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return 'N/A';
  return data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatarData = (valor) => {
  if (!valor) return 'N/A';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return 'N/A';
  return data.toLocaleDateString('pt-BR');
};

function KanbanItemCard({ item, index, onAction, actionTitle, actionType }) {
  const ActionIcon =
    actionType === 'diagnostico' ? InfoOutlinedIcon : ChatBubbleOutlineIcon;

  return (
    <Card
      sx={{
        mb: 2,
        backgroundColor: '#1e293b',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 2,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        transition: 'all 0.3s ease-in-out',
        opacity: 0,
        animation: `fadeInUp 0.5s ease-out forwards`,
        animationDelay: `${index * 50}ms`,
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
          borderColor: 'rgba(99, 91, 255, 0.8)',
        },
      }}
    >
      {onAction && (
        <IconButton
          aria-label={actionTitle || 'Abrir detalhes'}
          onClick={() => onAction(item)}
          title={actionTitle || 'Abrir detalhes'}
          sx={{
            position: 'absolute',
            top: 10,
            right: 13,
            color: '#fff',
            background: '#00678D',
            '&:hover': {
              background: '#00506b',
            },
            boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
            zIndex: 2,
            p: 0.8,
          }}
        >
          <ActionIcon fontSize="small" />
        </IconButton>
      )}
      <CardContent
        sx={{
          p: 2,
          pr: onAction ? 7 : 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1,
            wordBreak: 'break-word',
            pr: onAction ? 1 : 0,
            minHeight: 24,
          }}
        >
          {item.title}
        </Typography>
        <Box sx={{ mt: 1, fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          {item.description}
        </Box>
      </CardContent>
    </Card>
  );
}

function EmptyKanbanCard({ columnKey }) {
  const config = {
    'em-atendimento': {
      emoji: '🤝',
      text: 'Sem atendimentos no momento.',
    },
    finalizado: {
      emoji: '✅',
      text: 'Ainda não foi finalizado nenhum atendimento.',
    },
    'nao-atendidos': {
      emoji: '🚫',
      text: 'Nenhum atendimento ficou sem resposta.',
    },
  };
  const { emoji, text } = config[columnKey] || {
    emoji: '📝',
    text: 'Sem registros aqui.',
  };

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        color: '#bfc7d3',
        border: '1px dashed #635bff',
        backgroundColor: 'rgba(30,41,59,0.3)',
        borderRadius: 2,
        minHeight: 140,
        boxShadow: 'none',
        my: 2,
        py: 4,
        textAlign: 'center',
      }}
      elevation={0}
    >
      <Box sx={{ fontSize: 46, mb: 1 }}>{emoji}</Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
    </Card>
  );
}

function KanbanBoard({ clientesAtendidos, clientesNaoAtendidos }) {
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNaoAtendido, setSelectedNaoAtendido] = useState(null);
  const [openDiagnostico, setOpenDiagnostico] = useState(false);

  const { activeChats, chats, myUser } = useWSContext();
  const agora = useMemo(() => new Date(), []);
  const mesAnoAtual = useMemo(() => formatarMesAno(agora), [agora]);
  const inicioMesAtual = useMemo(() => startOfMonth(agora), [agora]);
  const fimMesAtual = useMemo(() => endOfMonth(agora), [agora]);

  const clientesAtendidosMesAtual = useMemo(() => {
    if (!Array.isArray(clientesAtendidos)) return [];
    return clientesAtendidos.filter((item) => {
      const dataRaw = item?.dataFim || item?.fimAtendimento || '';
      const data = new Date(dataRaw);
      if (Number.isNaN(data.getTime())) return false;
      return isWithinInterval(data, { start: inicioMesAtual, end: fimMesAtual });
    });
  }, [clientesAtendidos, inicioMesAtual, fimMesAtual]);

  const clientesNaoAtendidosMesAtual = useMemo(() => {
    if (!Array.isArray(clientesNaoAtendidos)) return [];
    return clientesNaoAtendidos.filter((item) => {
      const dataRaw = item?.timestamp || item?.dataSaida || item?.createdAt || '';
      const data = new Date(dataRaw);
      if (Number.isNaN(data.getTime())) return false;
      return isWithinInterval(data, { start: inicioMesAtual, end: fimMesAtual });
    });
  }, [clientesNaoAtendidos, inicioMesAtual, fimMesAtual]);

  const todosOsAtendimentos = useMemo(() => {
    const mapaDeAtendimentos = new Map();

    chats.forEach((item) => {
      const chatNormalizado = {
        chatId: item.id,
        cliente: {
          id: item.id,
          nome: item.name,
          numSerie: item.serialNumber,
          tipoEquipamento: 'Buscando...',
        },
        atendente: {
          nome: myUser?.nomeUsuario || 'Você',
        },
      };
      mapaDeAtendimentos.set(item.id, chatNormalizado);
    });

    activeChats.forEach((item) => {
      if (!mapaDeAtendimentos.has(item.chatId)) {
        mapaDeAtendimentos.set(item.chatId, item);
      }
    });

    return Array.from(mapaDeAtendimentos.values());
  }, [chats, activeChats, myUser]);

  const handleOpenHistoricoDireto = (cliente) => {
    const clienteId = cliente.clienteId || cliente.clientId || cliente.id;
    setSelectedCliente({
      clienteId,
      title: cliente.title,
    });
    setOpenDialog(true);
  };

  const handleOpenDiagnosticoNaoAtendido = (item) => {
    setSelectedNaoAtendido(item);
    setOpenDiagnostico(true);
  };

  const getCardActionConfig = (columnKey) => {
    if (columnKey === 'finalizado') {
      return {
        onAction: handleOpenHistoricoDireto,
        actionType: 'historico',
        actionTitle: 'Ver histórico do cliente',
      };
    }

    if (columnKey === 'nao-atendidos') {
      return {
        onAction: handleOpenDiagnosticoNaoAtendido,
        actionType: 'diagnostico',
        actionTitle: 'Ver diagnóstico do não atendimento',
      };
    }

    return {
      onAction: null,
      actionType: '',
      actionTitle: '',
    };
  };

  const getKanbanData = () => {
    const defaultData = {
      'em-atendimento': { title: 'Em atendimento', color: '#ffff00', items: [] },
      finalizado: { title: `Atendidos ${mesAnoAtual}`, color: '#10b981', items: [] },
      'nao-atendidos': {
        title: `Não atendidos ${mesAnoAtual}`,
        color: '#ef4444',
        items: [],
      },
    };
    const kanbanData = { ...defaultData };

    if (todosOsAtendimentos && Array.isArray(todosOsAtendimentos)) {
      kanbanData['em-atendimento'].items = todosOsAtendimentos.map(
        (item, index) => ({
          id: `atend-${item.chatId || item.cliente?.id || index}`,
          clienteId: item.cliente?.id || item.chatId,
          title: item.cliente?.nome || 'Cliente não identificado',
          description: (
            <Stack spacing={0.5} sx={{ fontSize: '0.8rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={dotSx} />
                <span>Atendido por: {item.atendente?.nome || 'N/A'}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={dotSx} />
                <span>NS: {item.cliente?.numSerie || 'N/A'}</span>
              </Box>
            </Stack>
          ),
        })
      );
    }

    if (clientesAtendidosMesAtual && Array.isArray(clientesAtendidosMesAtual)) {
      kanbanData.finalizado.items = clientesAtendidosMesAtual.map((item, index) => ({
        id: `fin-${item.clienteId || item.clientId || item.id}-${
          item.fimAtendimento || index
        }`,
        clienteId: item.clienteId || item.clientId || item.id,
        title: item.clienteNome || 'Cliente desconhecido',
        description: (
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Atendente:</b> {item.agenteNome || 'N/A'}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Nº Série:</b> {item.numSerie || 'N/A'}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Equipamento:</b> {item.tipoEquipamento || 'N/A'}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Duração:</b> {formatarDuracao(item.duracaoSegundos)}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Hora:</b> {formatarHora(item.dataFim || item.fimAtendimento)}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Data:</b> {formatarData(item.dataFim || item.fimAtendimento)}
              </span>
            </Box>
          </Stack>
        ),
      }));
    }

    if (clientesNaoAtendidosMesAtual && Array.isArray(clientesNaoAtendidosMesAtual)) {
      kanbanData['nao-atendidos'].items = clientesNaoAtendidosMesAtual.map((item, idx) => ({
        id: `na-${item.clientId || item.clienteId || item.id}-${
          item.timestamp || idx
        }`,
        clienteId: item.clienteId || item.clientId || item.id,
        clienteNome: item.clienteNome || item.nome || null,
        motivo: item.motivo || item.motivoSaida || null,
        numSerie: item.numSerie || null,
        tipoEquipamento: item.tipoEquipamento || null,
        contextoLogin: item.contextoLogin || null,
        diagnostico: item.diagnostico || null,
        timestamp: item.timestamp || item.dataSaida || null,
        title: item.clienteNome || 'Cliente desconhecido',
        description: (
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Motivo:</b>{' '}
                <span
                  title={item.motivo || 'N/A'}
                  style={{
                    display: 'inline-block',
                    maxWidth: '180px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'bottom',
                  }}
                >
                  {item.motivo || 'N/A'}
                </span>
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Tempo na fila:</b> {formatarTempoFila(item)}
              </span>
            </Box>
            {item.contextoLogin && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={dotSx} />
                <span>
                  <b>Atendentes online:</b>{' '}
                  {item.contextoLogin?.atendentesOnlineNoMomento ?? 0}
                </span>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Nº Série:</b> {item.numSerie || 'N/A'}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Equipamento:</b> {item.tipoEquipamento || 'N/A'}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Hora:</b> {formatarHora(item.timestamp || item.dataSaida)}
              </span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={dotSx} />
              <span>
                <b>Data:</b> {formatarData(item.timestamp || item.dataSaida)}
              </span>
            </Box>
          </Stack>
        ),
      }));
    }

    return kanbanData;
  };

  const kanbanData = getKanbanData();

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      <Grid item xs={12} sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
          Kanban de Atendimento
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {Object.entries(kanbanData).map(([key, column]) => {
            const itens = column.items;
            const actionConfig = getCardActionConfig(key);
            return (
              <Grid item xs={12} sm={10} md={6} lg={4} xl={4} key={key}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: 'rgba(30, 41, 59, 0.7)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pb: 1.5,
                        mb: 2,
                        borderBottom: `2px solid ${column.color}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: column.color,
                          mr: 1.5,
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {column.title}
                      </Typography>
                      <Box
                        sx={{
                          ml: 'auto',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '12px',
                          px: 1.5,
                          py: 0.5,
                          minWidth: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {column.items.length}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        maxHeight: 450,
                        overflowY: 'auto',
                        pr: 1.5,
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '10px',
                          border: '2px solid transparent',
                          backgroundClip: 'content-box',
                        },
                      }}
                    >
                      {itens.length === 0 ? (
                        <EmptyKanbanCard columnKey={key} />
                      ) : (
                        itens.map((item, index) => (
                          <KanbanItemCard
                            key={item.id}
                            item={item}
                            index={index}
                            onAction={actionConfig.onAction}
                            actionType={actionConfig.actionType}
                            actionTitle={actionConfig.actionTitle}
                          />
                        ))
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <ChatHistoryDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        clienteId={selectedCliente?.clienteId}
        clienteTitle={selectedCliente?.title}
      />
      <NaoAtendidoDiagnosticoDialog
        open={openDiagnostico}
        onClose={() => setOpenDiagnostico(false)}
        item={selectedNaoAtendido}
      />
    </>
  );
}

export default KanbanBoard;



